import fetchApi from 'src/util/fetchApi';
import checkErrors from 'src/api/checkErrors';
import useResourceLoader from 'src/util/useResourceLoader';
import { useAuthConfig } from 'src/context/AuthConfigContext';
import { OrgUnitTypeRecord, normalizeOrgUnitType } from 'src/util/orgUnitTypes';
import { SelectOption } from './types';

export type LookupResult<T> = { options: T[]; loading: boolean; error: Error | undefined };

export type OrgUnitItem = {
  id: number;
  name: string;
  parentId?: number | null;
  code?: string | null;
  organizationalUnitType?: { id: number; name: string } | null;
};

export type PermissionInfo = { name: string; description?: string };

function asArray(response: any): any[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function useLookup<T>(fetcher: () => Promise<T[]>): LookupResult<T> {
  const [options, loading, error] = useResourceLoader<T[]>(() => fetcher(), [], []);
  return { options: options ?? [], loading: !!loading, error };
}

/** User types (access profiles). The backend already hides profiles the viewer may not assign. */
export function useAccessProfileOptions(): LookupResult<SelectOption> {
  return useLookup(async () =>
    asArray(await checkErrors(await (fetchApi as any).lookup.get('profiles'))).map(profile => ({
      value: profile.id,
      label: profile.displayName || profile.name,
    }))
  );
}

export function useRoleOptions(): LookupResult<SelectOption> {
  return useLookup(async () =>
    asArray(await checkErrors(await (fetchApi as any).lookup.get('roles'))).map(role => ({ value: role.id, label: role.name }))
  );
}

export type UserOption = { id: number; name: string };

/** Every active user, for pickers. `name` is the "First Last" full name (what the workflow runtime matches on). */
export function useUserOptions(): LookupResult<UserOption> {
  return useLookup(async () =>
    asArray(await checkErrors(await (fetchApi as any).lookup.get('users', { useCache: false }))).map(user => ({
      id: user.id,
      name: user.completeName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    }))
  );
}

export function useTimeZoneOptions(): LookupResult<SelectOption> {
  return useLookup(async () =>
    asArray(await checkErrors(await (fetchApi as any).lookup.get('timezones'))).map(zone => ({ value: zone.id, label: zone.displayName || zone.id }))
  );
}

/** Org units a user can be placed in (`workable=1`), with parent links for the tree. Skipped when not `enabled`. */
export function useOrgUnits(enabled: boolean = true): LookupResult<OrgUnitItem> {
  const [options, loading, error] = useResourceLoader<OrgUnitItem[]>(
    async () =>
      enabled
        ? asArray(await checkErrors(await (fetchApi as any).lookup.get('organizationalunits', { query: { workable: 1 } }))).map(unit => ({
            id: unit.id,
            name: unit.name,
            parentId: unit.parentId ?? null,
          }))
        : [],
    [enabled],
    []
  );
  return { options: options ?? [], loading: !!loading, error };
}

/** Every live org unit type, sorted by name. Cached; OrganizationalUnitTypeApi drops the cache on every change. */
export async function fetchOrgUnitTypes(): Promise<OrgUnitTypeRecord[]> {
  const response: any = await checkErrors(
    await fetchApi.api.get('manage/organizationalunittype', { query: { offset: 0, size: 1000, sortBy: 'name' }, useCache: true })
  );
  const items = Array.isArray(response?.items) ? response.items : asArray(response);
  return items.filter((item: any) => !item.removed).map(normalizeOrgUnitType);
}

export function useOrgUnitTypes(): LookupResult<OrgUnitTypeRecord> {
  return useLookup(fetchOrgUnitTypes);
}

/**
 * The whole org tree as an administrator manages it, with each unit's type and code (the parent
 * picker filters by type, and a grouping level's code is derived from its parent's). Skipped when
 * not `enabled`, for the modes that never show the picker.
 */
export function useManagedOrgUnits(enabled: boolean = true): LookupResult<OrgUnitItem> {
  const [options, loading, error] = useResourceLoader<OrgUnitItem[]>(
    async () => {
      if (!enabled) return [];
      const response: any = await checkErrors(
        await fetchApi.api.get('manage/organizationalunit', { query: { all: true, size: 10000, sortBy: 'name' }, useCache: true })
      );
      return (Array.isArray(response?.items) ? response.items : []).map((unit: any) => ({
        id: unit.id,
        name: unit.name,
        parentId: unit.parentId ?? null,
        code: unit.code ?? null,
        organizationalUnitType: unit.organizationalUnitType ? { id: unit.organizationalUnitType.id, name: unit.organizationalUnitType.name } : null,
      }));
    },
    [enabled],
    []
  );
  return { options: options ?? [], loading: !!loading, error };
}

export function usePermissionOptions(): LookupResult<PermissionInfo> {
  return useLookup(async () =>
    asArray(await checkErrors(await fetchApi.api.get('manage/permissions', { query: { all: true }, useCache: true }))).map(permission => ({
      name: permission.name,
      description: permission.description,
    }))
  );
}

/** Login types offered by this environment. Legacy "External" accounts are shown and saved as Basic. */
export function useLoginTypeOptions(): SelectOption[] {
  const authConfig = useAuthConfig();
  return (authConfig?.typesList ?? []).map(loginType => ({
    value: loginType.type,
    label: `${loginType.label} (${loginType.type})`,
  }));
}
